/**
 * The map-frame view: everything the fleet actually knows a POSITION for, drawn
 * to scale in metres.
 *
 * ★★ Why this is not a map of the Earth. The prototype drew terminals on a
 *    lat/lng basemap at 31.2304N 121.4737E — Shanghai coordinates invented for
 *    the mock. The dog has no GPS indoors and its poses live in a SLAM `map`
 *    frame whose origin is wherever it booted. Placing them on a world map needs
 *    a surveyed anchor per site, and there is none yet
 *    (design-fleet-console-implementation.md §6c says explicitly: without the
 *    anchor, do not overlay — do not guess a position and draw it).
 *
 *    So this draws the frame that IS real: metres from the map origin, with a
 *    metric grid and a scale bar. Nothing here is projected onto anything.
 *
 * What is on screen, and where each thing comes from:
 *
 *   places    hub /knowledge/graph — the device's own named places (x, y in
 *             map frame, pushed by ksync after PIPL filtering)
 *   objects   same source — remembered object instances, sized by nObs
 *   device    hub /devices telemetry — poseX/poseY/poseYaw, WITH its own poseAt,
 *             so a stale pose is drawn as stale instead of as "here it is now"
 *
 * ★ Coordinates from different maps are NOT comparable: each SLAM map has its own
 *   origin. Hence the map selector — one map at a time, never a merged scatter.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { poseIsStale, fmtAge } from '@/lib/poseAge';

export interface MapPlace { name: string; x: number; y: number; deviceId: string; descr?: string }
export interface MapObject { name: string; x: number; y: number; nObs: number; deviceId: string; lastSeen: number }
export interface MapDevice {
  id: string;
  name: string;
  x: number;
  y: number;
  yaw: number | null;
  /** unix seconds of the pose itself — NOT of the last telemetry packet. */
  poseAt: number;
  online: boolean;
}

export interface MapGridLayer {
  bitmap: ImageBitmap;
  /** metres per pixel, and the map-frame position of pixel (0,0). */
  resolution: number;
  originX: number;
  originY: number;
  updatedAt: number;
  known: number;
}

interface Props {
  places: MapPlace[];
  objects: MapObject[];
  devices: MapDevice[];
  /**
   * 本次会话累积的轨迹(每台设备一串点)。
   *
   * ★ 它是【打开这个页面之后】从实时位姿流攒出来的, **不是历史存档** ——
   *   hub 不存轨迹, 刷新一下就从头开始。规格 §⑤ 要的"轨迹"里,能诚实给出的
   *   就是这一段;"时空回放"那条规格自己标着 ⬜(未建), 因为它需要归档。
   */
  trails?: Record<string, { x: number; y: number }[]>;
  /** 选中的设备(高亮 + 面板联动)。 */
  selectedDeviceId?: string | null;
  onSelectDevice?: (id: string | null) => void;
  /** The device's own occupancy grid — walls and free space, pushed by gridsync. */
  grid?: MapGridLayer | null;
  /** Selected object/place is drawn highlighted and kept in view. */
  focus?: { x: number; y: number } | null;
}

interface View { cx: number; cy: number; ppm: number }   // centre in metres, pixels per metre

/**
 * 把推上来的三态灰度图转成可叠的图层:未观测【透明】, 空地淡青, 障碍近黑。
 *
 * ★ 为什么未观测要透明而不是画成灰:一大片中灰会比背景更亮, 于是"没看过的地方"
 *   在视觉上反而最抢眼, 而它恰恰是**信息量为零**的那部分。透明之后, 屏幕上亮的
 *   就只有真的走通过的区域 —— 台面上的显著性要和信息量一致。
 * ★ 只在图更新时做一次(几十毫秒), 不放进每帧的绘制里。
 */
function useTintedGrid(grid: MapGridLayer | null | undefined) {
  const [tinted, setTinted] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!grid) { setTinted(null); return; }
    const c = document.createElement('canvas');
    c.width = grid.bitmap.width; c.height = grid.bitmap.height;
    const cx = c.getContext('2d', { willReadFrequently: true });
    if (!cx) { setTinted(null); return; }
    cx.drawImage(grid.bitmap, 0, 0);
    const img = cx.getImageData(0, 0, c.width, c.height);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = d[i];
      if (v > 200) {            // free
        d[i] = 186; d[i + 1] = 230; d[i + 2] = 253; d[i + 3] = 235;
      } else if (v < 60) {      // occupied
        d[i] = 15; d[i + 1] = 23; d[i + 2] = 42; d[i + 3] = 255;
      } else {                  // unknown → 透明
        d[i + 3] = 0;
      }
    }
    cx.putImageData(img, 0, 0);
    setTinted(c);
  }, [grid]);
  return tinted;
}

export default function MapFrameView({ places, objects, devices, grid, trails, selectedDeviceId, onSelectDevice, focus }: Props) {
  const tinted = useTintedGrid(grid);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View | null>(null);
  const viewRef = useRef<View | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState<{ label: string; sub: string; sx: number; sy: number } | null>(null);
  viewRef.current = view;

  /** Content bounds in metres — the fit-to-content the first paint uses. */
  const bounds = useMemo(() => {
    const xs: number[] = [], ys: number[] = [];
    places.forEach(p => { xs.push(p.x); ys.push(p.y); });
    objects.forEach(o => { xs.push(o.x); ys.push(o.y); });
    devices.forEach(d => { xs.push(d.x); ys.push(d.y); });
    if (grid) {
      // 取景要把【地图本身】算进去 —— 只框住散点的话, 走廊尽头那半张图就在屏幕外,
      // 而"这是哪个房间"恰恰要看整张图才答得出。
      xs.push(grid.originX, grid.originX + grid.bitmap.width * grid.resolution);
      ys.push(grid.originY, grid.originY + grid.bitmap.height * grid.resolution);
    }
    if (!xs.length) return null;
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  }, [places, objects, devices, grid]);

  /* Fit once per content change, but never fight a user who has panned/zoomed. */
  const fittedRef = useRef<string>('');
  useEffect(() => {
    if (!bounds || !boxRef.current) return;
    const key = `${places.length}/${objects.length}/${devices.length}/${grid?.updatedAt ?? 0}/${bounds.minX.toFixed(1)}/${bounds.maxX.toFixed(1)}`;
    if (fittedRef.current === key) return;
    fittedRef.current = key;
    const w = boxRef.current.clientWidth || 800;
    const h = boxRef.current.clientHeight || 500;
    const spanX = Math.max(2, bounds.maxX - bounds.minX);
    const spanY = Math.max(2, bounds.maxY - bounds.minY);
    // 12% margin so labels at the edge are not clipped.
    const ppm = Math.min(w / (spanX * 1.24), h / (spanY * 1.24));
    setView({ cx: (bounds.minX + bounds.maxX) / 2, cy: (bounds.minY + bounds.maxY) / 2, ppm: Math.max(4, Math.min(120, ppm)) });
  }, [bounds, places.length, objects.length, devices.length, grid?.updatedAt]);

  /* Focus request (clicked a row in the side panel): centre on it, keep the zoom. */
  useEffect(() => {
    if (!focus || !viewRef.current) return;
    setView(v => (v ? { ...v, cx: focus.x, cy: focus.y } : v));
  }, [focus]);

  /* ── draw ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const box = boxRef.current;
    if (!canvas || !box || !view) return;
    const dpr = window.devicePixelRatio || 1;
    const w = box.clientWidth, h = box.clientHeight;
    canvas.width = w * dpr; canvas.height = h * dpr;
    canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // map frame -> screen. y grows "up" in the map frame, down on screen.
    const sx = (x: number) => w / 2 + (x - view.cx) * view.ppm;
    const sy = (y: number) => h / 2 - (y - view.cy) * view.ppm;

    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, w, h);

    /* ★ 占据栅格先画 —— 它是底图, 其它一切都叠在它上面。
       图像的一个像素 = resolution 米, 左下角在 (originX, originY);
       PNG 的行是自上而下的, 而 gridsync 在编码时已经翻过 y, 所以这里直接贴。 */
    if (grid && tinted) {
      const gw = grid.bitmap.width * grid.resolution * view.ppm;
      const gh = grid.bitmap.height * grid.resolution * view.ppm;
      const gx = sx(grid.originX);
      const gy = sy(grid.originY + grid.bitmap.height * grid.resolution);
      ctx.save();
      ctx.imageSmoothingEnabled = false;   // 栅格是离散的, 插值会把一格宽的墙糊成灰边
      ctx.drawImage(tinted, gx, gy, gw, gh);
      ctx.restore();
    }

    /* Metric grid. Step chosen so lines stay ~40-140 px apart at any zoom —
       a grid whose spacing is not stated in metres is decoration; this one is
       labelled, so distances can be read straight off the canvas. */
    const targetPx = 80;
    const rawStep = targetPx / view.ppm;
    const pow = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step = [1, 2, 5, 10].map(m => m * pow).find(s => s * view.ppm >= 40) ?? 10 * pow;
    const x0 = Math.floor((view.cx - w / 2 / view.ppm) / step) * step;
    const x1 = view.cx + w / 2 / view.ppm;
    const y0 = Math.floor((view.cy - h / 2 / view.ppm) / step) * step;
    const y1 = view.cy + h / 2 / view.ppm;
    ctx.strokeStyle = 'rgba(148,163,184,0.10)';
    ctx.lineWidth = 1;
    ctx.font = '9px "JetBrains Mono", monospace';
    ctx.fillStyle = 'rgba(148,163,184,0.45)';
    for (let x = x0; x <= x1; x += step) {
      const px = Math.round(sx(x)) + 0.5;
      ctx.beginPath(); ctx.moveTo(px, 0); ctx.lineTo(px, h); ctx.stroke();
      // x 轴刻度画在【上边】: 下边被"拖拽平移/滚轮缩放"那行提示占着, 两者会叠在一起。
      ctx.fillText(`${x.toFixed(step < 1 ? 1 : 0)}`, px + 3, 11);
    }
    for (let y = y0; y <= y1; y += step) {
      const py = Math.round(sy(y)) + 0.5;
      ctx.beginPath(); ctx.moveTo(0, py); ctx.lineTo(w, py); ctx.stroke();
      ctx.fillText(`${y.toFixed(step < 1 ? 1 : 0)}`, 4, py - 3);
    }

    /* Map origin — the one point every coordinate here is relative to. */
    const ox = sx(0), oy = sy(0);
    if (ox > -50 && ox < w + 50 && oy > -50 && oy < h + 50) {
      ctx.strokeStyle = 'rgba(56,189,248,0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(ox - 9, oy); ctx.lineTo(ox + 9, oy);
      ctx.moveTo(ox, oy - 9); ctx.lineTo(ox, oy + 9); ctx.stroke();
      ctx.fillStyle = 'rgba(56,189,248,0.7)';
      // 画在左下角:地点名一律画在点的【上方】, 原点标签放上面必然打架。
      ctx.fillText('map 原点 (0,0)', ox + 11, oy + 14);
    }

    /* 轨迹:本次会话攒的。画得细而淡 —— 它是辅助线索, 不该盖过地图本身。 */
    if (trails) {
      for (const pts of Object.values(trails)) {
        if (pts.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(sx(pts[0].x), sy(pts[0].y));
        for (let i = 1; i < pts.length; i++) ctx.lineTo(sx(pts[i].x), sy(pts[i].y));
        ctx.strokeStyle = 'rgba(34,211,238,0.55)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    /* Objects: small dots, radius by sqrt(nObs) — the same "how often was it
       actually seen" that sizes nodes on the knowledge page. Never a fixed size:
       an instance seen once and one seen forty times are not equally real. */
    objects.forEach(o => {
      const r = 2.5 + Math.min(4, Math.sqrt(Math.max(1, o.nObs)));
      ctx.beginPath();
      ctx.arc(sx(o.x), sy(o.y), r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(245,158,11,0.55)';
      ctx.fill();
    });

    /* Places: labelled pins. */
    places.forEach(p => {
      const px = sx(p.x), py = sy(p.y);
      ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#f97316'; ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.font = '600 11px "Inter", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(11,16,32,0.9)';
      ctx.strokeText(p.name, px, py - 9);
      ctx.fillStyle = '#fed7aa';
      ctx.fillText(p.name, px, py - 9);
      ctx.textAlign = 'left';
    });

    /* Devices. A pose older than POSE_STALE_S is drawn HOLLOW and labelled with
       its age: "where it was 20 minutes ago" and "where it is" must not look the
       same (2026-08-08: the pose gate latched for 44 minutes while every other
       layer looked healthy). */
    const nowS = Date.now() / 1000;
    devices.forEach(d => {
      const px = sx(d.x), py = sy(d.y);
      const ageS = nowS - d.poseAt;
      // 判据在 lib/poseAge.ts, 带用例 —— 它守的是"最后已知位置"不许长得像"此刻在哪"。
      const stale = poseIsStale(d.poseAt, nowS);
      const color = stale ? 'rgba(148,163,184,0.9)' : '#22d3ee';
      if (d.yaw != null) {
        // Heading wedge — a robot's facing is half of "where it is".
        ctx.beginPath();
        ctx.moveTo(px, py);
        const a0 = -d.yaw - 0.34, a1 = -d.yaw + 0.34;
        ctx.arc(px, py, 26, a0, a1);
        ctx.closePath();
        ctx.fillStyle = stale ? 'rgba(148,163,184,0.10)' : 'rgba(34,211,238,0.18)';
        ctx.fill();
      }
      if (d.id === selectedDeviceId) {
        ctx.beginPath(); ctx.arc(px, py, 13, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 1.5; ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2);
      if (stale) { ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.stroke(); }
      else { ctx.fillStyle = color; ctx.fill(); }
      ctx.font = '600 11px "Inter", system-ui, sans-serif';
      ctx.textAlign = 'center';
      const age = stale ? `  ${fmtAge(ageS)}前` : '';
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(11,16,32,0.9)';
      ctx.strokeText(d.name + age, px, py + 20);
      ctx.fillStyle = stale ? '#cbd5e1' : '#a5f3fc';
      ctx.fillText(d.name + age, px, py + 20);
      ctx.textAlign = 'left';
    });

    /* Scale bar — the whole point of drawing in metres is being able to measure. */
    const barM = step;
    const barPx = barM * view.ppm;
    const bx = w - barPx - 16, by = h - 22;
    ctx.strokeStyle = 'rgba(226,232,240,0.8)'; ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(bx, by - 4); ctx.lineTo(bx, by); ctx.lineTo(bx + barPx, by); ctx.lineTo(bx + barPx, by - 4);
    ctx.stroke();
    ctx.font = '10px "JetBrains Mono", monospace';
    ctx.fillStyle = 'rgba(226,232,240,0.9)';
    ctx.textAlign = 'center';
    ctx.fillText(`${barM} m`, bx + barPx / 2, by - 7);
    ctx.textAlign = 'left';
  }, [view, places, objects, devices, grid, tinted, trails, selectedDeviceId]);

  /** 屏幕坐标 → 米(以及画布内像素, 给 tooltip 定位用)。 */
  const toMetres = useCallback((clientX: number, clientY: number) => {
    const box = boxRef.current; const v = viewRef.current;
    if (!box || !v) return null;
    const r = box.getBoundingClientRect();
    return {
      x: v.cx + (clientX - r.left - r.width / 2) / v.ppm,
      y: v.cy - (clientY - r.top - r.height / 2) / v.ppm,
      px: clientX - r.left, py: clientY - r.top,
    };
  }, []);

  /** 悬停探针:指到什么就说什么。用 ref 交给下面的原生监听 —— 那些监听只装一次,
   *  不能闭包住每次渲染的 places/objects/devices。 */
  const hoverProbeRef = useRef<((cx: number, cy: number) => void) | null>(null);
  const pickRef = useRef<((cx: number, cy: number) => void) | null>(null);
  pickRef.current = (clientX, clientY) => {
    const v = viewRef.current;
    const m = toMetres(clientX, clientY);
    if (!v || !m || !onSelectDevice) return;
    const hit = devices.find(t => Math.hypot((t.x - m.x) * v.ppm, (t.y - m.y) * v.ppm) < 16);
    onSelectDevice(hit ? hit.id : null);
  };
  hoverProbeRef.current = (clientX, clientY) => {
    const v = viewRef.current;
    const m = toMetres(clientX, clientY);
    if (!v || !m) return;
    // 14 像素内最近的那个 —— 与缩放无关, 手感才稳定。
    const near = (x: number, y: number) => Math.hypot((x - m.x) * v.ppm, (y - m.y) * v.ppm) < 14;
    const d = devices.find(t => near(t.x, t.y));
    if (d) {
      setHover({ label: d.name, sub: `${d.x.toFixed(2)}, ${d.y.toFixed(2)} m · 位姿 ${fmtAge(Date.now() / 1000 - d.poseAt)}前`, sx: m.px, sy: m.py });
      return;
    }
    const p = places.find(t => near(t.x, t.y));
    if (p) { setHover({ label: p.name, sub: `地点 · ${p.x.toFixed(2)}, ${p.y.toFixed(2)} m`, sx: m.px, sy: m.py }); return; }
    const o = objects.find(t => near(t.x, t.y));
    if (o) { setHover({ label: o.name, sub: `物体 · 观测 ${o.nObs} 次 · ${o.x.toFixed(2)}, ${o.y.toFixed(2)} m`, sx: m.px, sy: m.py }); return; }
    setHover(null);
  };

  /* 平移/点选。★ 用 pointer 事件 + setPointerCapture, 不用 React 的 onMouseMove:
     快速拖出画布时 React 那套会停止收事件, 再进来 delta 跳一大截 —— 表现为顿挫。
     这条和 6px 的"点击 vs 拖拽"阈值都照抄座舱(Map3DView), 两个界面手感一致。 */
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    let dragging = false, moved = false, ax = 0, ay = 0;
    const down = (e: PointerEvent) => {
      dragging = true; moved = false; ax = e.clientX; ay = e.clientY;
      try { box.setPointerCapture(e.pointerId); } catch { /* 老浏览器 */ }
    };
    const move = (e: PointerEvent) => {
      const v = viewRef.current;
      if (!v || !dragging) {
        if (!dragging) hoverProbeRef.current?.(e.clientX, e.clientY);
        return;
      }
      const dx = e.clientX - ax, dy = e.clientY - ay;
      if (!moved && Math.abs(dx) <= 6 && Math.abs(dy) <= 6) return;
      moved = true;
      ax = e.clientX; ay = e.clientY;
      setView({ ...v, cx: v.cx - dx / v.ppm, cy: v.cy + dy / v.ppm });
    };
    const up = (e: PointerEvent) => {
      // 位移小于阈值 = 点击:选中最近的设备(没点中就取消选中)。与座舱同一条契约。
      if (dragging && !moved) pickRef.current?.(e.clientX, e.clientY);
      dragging = false;
      try { box.releasePointerCapture(e.pointerId); } catch { /* 已释放 */ }
    };
    // 双指:中点平移 + 捏合缩放(手机上没有滚轮)。
    let tmx = 0, tmy = 0, tdist = 0;
    const tStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      tmx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      tmy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      tdist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    };
    const tMove = (e: TouchEvent) => {
      const v = viewRef.current;
      if (!v || e.touches.length !== 2) return;
      e.preventDefault();
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      const ppm = tdist > 0 && d > 0 ? Math.max(2, Math.min(300, v.ppm * (d / tdist))) : v.ppm;
      setView({ ppm, cx: v.cx - (mx - tmx) / v.ppm, cy: v.cy + (my - tmy) / v.ppm });
      tmx = mx; tmy = my; tdist = d;
    };
    const leave = () => setHover(null);
    box.addEventListener('pointerdown', down);
    box.addEventListener('pointermove', move);
    box.addEventListener('pointerup', up);
    box.addEventListener('pointercancel', up);
    box.addEventListener('pointerleave', leave);
    box.addEventListener('touchstart', tStart, { passive: true });
    box.addEventListener('touchmove', tMove, { passive: false });
    return () => {
      box.removeEventListener('pointerdown', down);
      box.removeEventListener('pointermove', move);
      box.removeEventListener('pointerup', up);
      box.removeEventListener('pointercancel', up);
      box.removeEventListener('pointerleave', leave);
      box.removeEventListener('touchstart', tStart);
      box.removeEventListener('touchmove', tMove);
    };
  }, []);

  /* 滚轮缩放。★ 必须用原生监听并显式 passive:false —— React 的 onWheel 落在
     passive 监听里, e.preventDefault() 无效(控制台直接报错), 结果是缩放的同时
     整页跟着滚。 */
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const onWheel = (e: WheelEvent) => {
      const v = viewRef.current;
      if (!v) return;
      e.preventDefault();
      const m = toMetres(e.clientX, e.clientY);
      const k = e.deltaY > 0 ? 1 / 1.12 : 1.12;
      const ppm = Math.max(2, Math.min(300, v.ppm * k));
      // 以光标为中心缩放, 不是画布中心 —— 否则放大一个角落等于满屏追着它跑。
      if (m) setView({ ppm, cx: m.x - (m.x - v.cx) * (v.ppm / ppm), cy: m.y - (m.y - v.cy) * (v.ppm / ppm) });
      else setView({ ...v, ppm });
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, [toMetres]);

  /* Redraw on container resize — without this the canvas keeps the old size and
     the whole scene silently shifts off centre. */
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const ro = new ResizeObserver(() => setView(v => (v ? { ...v } : v)));
    ro.observe(box);
    return () => ro.disconnect();
  }, []);


  return (
    <div
      ref={boxRef}
      className="relative flex-1 min-h-[320px] rounded-lg overflow-hidden border border-background-200/70 bg-[#0b1020] cursor-grab active:cursor-grabbing touch-none"
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
      {hover && (
        <div className="absolute z-10 pointer-events-none px-2 py-1 rounded bg-slate-800/95 border border-slate-600/60"
          style={{ left: Math.min(hover.sx + 12, 9999), top: hover.sy + 12 }}>
          <div className="text-[11px] font-semibold text-slate-100">{hover.label}</div>
          <div className="text-[10px] text-slate-400 font-mono">{hover.sub}</div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-2 flex items-center gap-3 text-[10px] text-slate-400 pointer-events-none">
        <span className="inline-flex items-center gap-1"><i className="ri-drag-move-2-line" />拖拽平移</span>
        <span className="inline-flex items-center gap-1"><i className="ri-zoom-in-line" />滚轮缩放</span>
        {view && <span className="font-mono">{view.ppm.toFixed(0)} px/m</span>}
      </div>
    </div>
  );
}
